import React from "react";
import PropTypes from "prop-types";

// Catches render/lifecycle errors so one broken component cannot blank the whole
// app. Before this existed, any throw during render unmounted the entire tree —
// e.g. a wrapped `window.scroll` returning a non-function cleanup took down the
// FAQ route completely (see QA_FINDINGS.md, batch 7).
//
// Must be a class: React exposes no hook equivalent of componentDidCatch.
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        const { onError, name } = this.props;

        // Surface to analytics when available, so these stop being invisible in
        // production (where the console is silenced by index.js).
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
            try {
                window.gtag("event", "exception", {
                    description: `${name || "app"}: ${error?.message || error}`,
                    fatal: false,
                });
            } catch {
                // Analytics must never mask the original error.
            }
        }

        if (typeof onError === "function") {
            onError(error, errorInfo);
        }
    }

    // Lets a parent clear the error state, e.g. on route change, so a user is
    // not stuck on the fallback for the rest of the session.
    reset = () => {
        this.setState({ hasError: false });
    };

    render() {
        const { hasError } = this.state;
        const { children, fallback } = this.props;

        if (!hasError) {
            return children;
        }

        if (typeof fallback === "function") {
            return fallback(this.reset);
        }

        // `null` is a deliberate default: a subtree that fails simply does not
        // render, rather than replacing the page with an error screen.
        return fallback !== undefined ? fallback : null;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node,
    /** Static node, or a function receiving a `reset` callback. */
    fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /** Called with (error, errorInfo) when a child throws. */
    onError: PropTypes.func,
    /** Label used in the analytics exception description. */
    name: PropTypes.string,
};

export default ErrorBoundary;
